import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const ParentDashboard = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role !== 'parent') {
      // Redirect to appropriate dashboard
      if (user.role === 'admin') navigate('/admin/dashboard');
      else if (user.role === 'teacher') navigate('/teacher/dashboard');
      else if (user.role === 'student') navigate('/dashboard');
    }
  }, [user, navigate]);

  const parentSections = [
    {
      id: 'children',
      icon: '👧👦',
      title: language === 'tr' ? 'Çocuklarım' : 'My Children',
      description: language === 'tr' ? 'Çocuklarınızın listesi ve genel durumu' : 'List of your children and their status',
      path: '/parent/children',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'progress',
      icon: '📈',
      title: language === 'tr' ? 'İlerleme Raporları' : 'Progress Reports',
      description: language === 'tr' ? 'Detaylı performans analizi' : 'Detailed performance analysis',
      path: '/parent/reports',
      gradient: 'from-green-500 to-teal-500'
    },
    {
      id: 'activities',
      icon: '📅',
      title: language === 'tr' ? 'Çalışma Aktiviteleri' : 'Study Activities',
      description: language === 'tr' ? 'Günlük ve haftalık aktiviteler' : 'Daily and weekly activities',
      path: '/parent/activities',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      id: 'goals',
      icon: '🎯',
      title: language === 'tr' ? 'Hedefler' : 'Goals',
      description: language === 'tr' ? 'Öğrenme hedefleri ve başarılar' : 'Learning goals and achievements',
      path: '/parent/goals',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Ebeveyn Paneli' : 'Parent Panel'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                👨‍👩‍👧 {language === 'tr' ? 'Hoş Geldiniz' : 'Welcome'}
              </h1>
              <p className="text-green-100 text-lg">
                {language === 'tr' 
                  ? 'Çocuğunuzun eğitim sürecini takip edin'
                  : 'Track your child\'s educational journey'}
              </p>
            </div>
            <div className="text-6xl">📚</div>
          </div>
        </div>

        {/* Coming Soon Notice */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-8 mb-8 text-center">
          <div className="text-6xl mb-4">🚧</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            {language === 'tr' ? 'Çok Yakında!' : 'Coming Soon!'}
          </h2>
          <p className="text-gray-600 mb-6">
            {language === 'tr'
              ? 'Ebeveyn paneli özellikleri şu anda geliştiriliyor. Çok yakında çocuğunuzun ilerlemesini detaylı bir şekilde takip edebileceksiniz.'
              : 'Parent panel features are currently under development. Soon you\'ll be able to track your child\'s progress in detail.'}
          </p>
        </div>

        {/* Preview Sections */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {language === 'tr' ? '🔮 Yakında Gelenler' : '🔮 Coming Features'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {parentSections.map((section) => (
              <div
                key={section.id}
                className="bg-white rounded-2xl shadow-lg p-6 opacity-75 cursor-not-allowed"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`inline-flex p-4 bg-gradient-to-br ${section.gradient} rounded-2xl text-4xl shadow-lg opacity-50`}>
                    {section.icon}
                  </div>
                  <span className="bg-gray-200 text-gray-600 px-3 py-1 rounded-full text-xs font-semibold">
                    {language === 'tr' ? 'YAKINDA' : 'SOON'}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {section.title}
                </h3>
                
                <p className="text-sm text-gray-600">
                  {section.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Planned Features */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            ✨ {language === 'tr' ? 'Planl anan Özellikler' : 'Planned Features'}
          </h3>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-green-500 text-xl mr-3">✓</span>
              <div>
                <div className="font-semibold text-gray-800">
                  {language === 'tr' ? 'Gerçek Zamanlı İlerleme Takibi' : 'Real-time Progress Tracking'}
                </div>
                <div className="text-sm text-gray-600">
                  {language === 'tr' 
                    ? 'Çocuğunuzun tüm aktivitelerini anlık olarak görün'
                    : 'View all your child\'s activities in real-time'}
                </div>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 text-xl mr-3">✓</span>
              <div>
                <div className="font-semibold text-gray-800">
                  {language === 'tr' ? 'Haftalık Performans Raporları' : 'Weekly Performance Reports'}
                </div>
                <div className="text-sm text-gray-600">
                  {language === 'tr'
                    ? 'Detaylı analiz ve önerilerle haftalık raporlar'
                    : 'Weekly reports with detailed analysis and recommendations'}
                </div>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 text-xl mr-3">✓</span>
              <div>
                <div className="font-semibold text-gray-800">
                  {language === 'tr' ? 'Zayıf Konular Bildirimi' : 'Weak Topics Notification'}
                </div>
                <div className="text-sm text-gray-600">
                  {language === 'tr'
                    ? 'Çocuğunuzun hangi konularda desteğe ihtiyacı olduğunu öğrenin'
                    : 'Learn which topics your child needs support in'}
                </div>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-green-500 text-xl mr-3">✓</span>
              <div>
                <div className="font-semibold text-gray-800">
                  {language === 'tr' ? 'Öğretmen ile İletişim' : 'Teacher Communication'}
                </div>
                <div className="text-sm text-gray-600">
                  {language === 'tr'
                    ? 'Doğrudan öğretmenle mesajlaşma ve geri bildirim'
                    : 'Direct messaging with teachers and feedback'}
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ParentDashboard;
